/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Storage utils
 *
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @version 1.3.0
 * @since 1.0.0
 */
/**
 * A function of receiving a cookie by name
 * @param {string} name - cookie name
 * @returns {string | undefined} - value
 */
export const getCookieByName = (name: string): any => {
    function escape(s: string) {
        return s.replace(/([.*+?\^${}()|\[\]\/\\])/g, '\\$1');
    }
    const match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : '';
};

export const getCookieByNameAsJson = (name: string, def: any): any => {
    function escape(s: string) {
        return s.replace(/([.*+?\^${}()|\[\]\/\\])/g, '\\$1');
    }
    const match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? JSON.parse(match[1]) : def;
};

export const setCookie = (name: string, value: string, options: any = {}) => {
    options = {
        path: '/',
        ...options,
    };

    let updatedCookie = encodeURIComponent(name) + '=' + encodeURIComponent(value);

    for (const optionKey in options) {
        updatedCookie += '; ' + optionKey;
        const optionValue = options[optionKey];
        if (optionValue !== true) {
            updatedCookie += '=' + optionValue;
        }
    }
    document.cookie = updatedCookie;
};

/**
 * This feature is required to remove cookies from local storage
 * @param {string} name
 */
export const removeCookie = (name: string): void => {
    setCookie(name, '', { 'max-age': -1 });
};

/**
 * Method for retrieving data from local storage by name
 * @param key {string} - name of the item in the store
 */
export const getItemFromLocalStorage = (key: string): string => {
    try {
        const serializedState = localStorage.getItem(key);
        if (serializedState === null) {
            return '';
        }
        return JSON.parse(serializedState);
    } catch (e) {
        return '';
    }
};

/**
 * Method of writing data to local storage
 * @param key {string} - name of item
 * @param value {string} - item of value
 */
export const setItemToLocalStorage = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch (error) {
        // ignore write errors
    }
};

/**
 * Remove item from local storage
 *
 * @param key {string} - name of item
 */
export const removeItemLocalStorage = (key: string): void => {
    localStorage.removeItem(key);
};

/**
 *  Method for retrieving data from session storage by name
 * @param {string} key
 * @returns {string | undefined}
 */
export const getItemFromSessionStorage = (key: string) => {
    try {
        const serializedState = sessionStorage.getItem(key);
        if (serializedState === null) {
            return [];
        }
        return JSON.parse(serializedState);
    } catch (e) {
        return [];
    }
};

export const setItemFromSessionStorage = (key: string, value: any): void => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        // ignore write errors
    }
};
